import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styles from './AdminOrderSelect.module.css';

const MENU_MAX_WIDTH = 420;
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 12;

function statusLabel(status) {
    return status === 'production' ? 'Production' : 'Delivering';
}

function orderTitle(order) {
    const name = String(order?.name || '').trim();
    return name ? `${order.id} · ${name}` : String(order?.id || '');
}

function StatusTag({status}) {
    return (
        <span className={`${styles.status} ${status === 'production' ? styles.statusProduction : styles.statusDelivering}`}>
            {statusLabel(status)}
        </span>
    );
}

export default function AdminOrderSelect({id, orders, value, onChange}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = orders.find((order) => order.id === value) || null;
    const normalizedQuery = query.trim().toLowerCase();
    const visibleOrders = orders.filter((order) => (
        !normalizedQuery || `${order.id} ${order.name || ''}`.toLowerCase().includes(normalizedQuery)
    ));

    const close = (restoreFocus = false) => {
        setOpen(false);
        if (restoreFocus) triggerRef.current?.focus();
    };

    const choose = (order) => {
        onChange(order.id);
        close(true);
    };

    useEffect(() => {
        if (!open) return;
        setQuery('');
        const selectedIndex = orders.findIndex((order) => order.id === value);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        inputRef.current?.focus();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        if (!open) return undefined;
        // The menu lives in a portal so ancestors with overflow: clip cannot cut it off; position it against the trigger.
        const position = () => {
            const trigger = triggerRef.current;
            const menu = menuRef.current;
            if (!trigger || !menu) return;
            const rect = trigger.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
            const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
            const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow;
            const width = Math.min(Math.max(rect.width, 300), MENU_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
            const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
            menu.style.setProperty('--aos-left', `${left}px`);
            menu.style.setProperty('--aos-width', `${width}px`);
            menu.style.setProperty('--aos-max-height', `${Math.max(openAbove ? spaceAbove : spaceBelow, 120)}px`);
            menu.style.setProperty('--aos-top', openAbove ? 'auto' : `${rect.bottom + MENU_GAP}px`);
            menu.style.setProperty('--aos-bottom', openAbove ? `${window.innerHeight - rect.top + MENU_GAP}px` : 'auto');
        };
        position();
        window.addEventListener('scroll', position, true);
        window.addEventListener('resize', position);
        return () => {
            window.removeEventListener('scroll', position, true);
            window.removeEventListener('resize', position);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleMouseDown = (event) => {
            if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
            close();
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({block: 'nearest'});
    }, [activeIndex, open]);

    const handleMenuKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((current) => Math.min(current + 1, visibleOrders.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((current) => Math.max(current - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (visibleOrders[activeIndex]) choose(visibleOrders[activeIndex]);
        } else if (event.key === 'Tab') {
            close();
        }
    };

    const listboxId = `${id}-listbox`;
    const selectedName = String(selected?.name || '').trim();

    return (
        <>
            <button
                ref={triggerRef}
                id={id}
                type="button"
                className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
                onClick={() => setOpen((current) => !current)}
                onKeyDown={(event) => {
                    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                title={selected ? orderTitle(selected) : undefined}
            >
                {selected ? (
                    <span className={styles.triggerText}>
                        <span className={styles.orderId}>{selected.id}</span>
                        {selectedName ? <span className={styles.triggerName}>· {selectedName}</span> : null}
                    </span>
                ) : (
                    <span className={`${styles.triggerText} ${styles.placeholder}`}>Select an Order…</span>
                )}
                {selected ? <StatusTag status={selected.status}/> : null}
                <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>
            {open ? ReactDOM.createPortal(
                <div ref={menuRef} className={styles.menu} onKeyDown={handleMenuKeyDown}>
                    <div className={styles.search}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setActiveIndex(0);
                            }}
                            placeholder="Search order ID or name…"
                            aria-label="Search order ID or name"
                            aria-controls={listboxId}
                            aria-activedescendant={visibleOrders[activeIndex] ? `${listboxId}-${activeIndex}` : undefined}
                        />
                    </div>
                    <div ref={listRef} id={listboxId} className={styles.options} role="listbox" aria-label="Orders">
                        {visibleOrders.map((order, index) => {
                            const name = String(order.name || '').trim();
                            return (
                                <div
                                    key={order.id}
                                    id={`${listboxId}-${index}`}
                                    data-index={index}
                                    role="option"
                                    aria-selected={order.id === value}
                                    title={orderTitle(order)}
                                    className={`${styles.option} ${order.id === value ? styles.optionSelected : ''} ${index === activeIndex ? styles.optionActive : ''}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => choose(order)}
                                >
                                    <span className={styles.optionTop}>
                                        <span className={styles.orderId}>{order.id}</span>
                                        <StatusTag status={order.status}/>
                                    </span>
                                    {name ? <span className={styles.optionName}>{name}</span> : null}
                                </div>
                            );
                        })}
                        {!visibleOrders.length ? (
                            <div className={styles.empty}>
                                {orders.length ? `No orders match "${query}"` : 'No Production or Delivering Orders'}
                            </div>
                        ) : null}
                    </div>
                </div>,
                document.body,
            ) : null}
        </>
    );
}
